import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCircle2, AlertTriangle, Clock, Loader2, Search,
  Shield, RefreshCw, ChevronDown, ChevronRight, Archive, CalendarDays, RotateCw
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active:                 { label: 'Attivo',              color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  active_partial:         { label: 'Parziale',            color: 'bg-amber-500/15 text-amber-400 border-amber-500/30',       icon: AlertTriangle },
  pending_implementation: { label: 'Da implementare',     color: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',         icon: Clock },
  pending_activation:     { label: 'Da attivare',         color: 'bg-blue-500/15 text-blue-400 border-blue-500/30',         icon: Clock },
  future:                 { label: 'Futuro',              color: 'bg-purple-500/15 text-purple-400 border-purple-500/30',    icon: Clock },
};

const HEALTH_CONFIG = {
  ok:                     { label: 'OK',                  color: 'text-emerald-400' },
  error:                  { label: 'Errore',              color: 'text-red-400' },
  not_configured:         { label: 'Non configurato',     color: 'text-amber-400' },
  pending_implementation: { label: 'Non implementato',    color: 'text-zinc-500' },
  pending_activation:     { label: 'Non attivato',        color: 'text-blue-400' },
  future:                 { label: 'Futuro',              color: 'text-purple-400' },
};

const AREAS = ['Pubblicazione', 'Contenuti', 'CRM', 'Eventi', 'Vendite', 'Funnel', 'Partnership', 'Intelligence', 'Design', 'Ads'];

export default function TaskRegistryPage() {
  const [tasks, setTasks] = useState([]);
  const [customTasks, setCustomTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('sistema');
  const [summary, setSummary] = useState(null);
  const [channels, setChannels] = useState([]);
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);
  const [filterArea, setFilterArea] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAreas, setExpandedAreas] = useState({});
  const [healthMode, setHealthMode] = useState(false);

  const fetchRegistry = useCallback(async () => {
    try {
      const { data } = await api.get('/system/task-registry');
      setTasks(data.tasks);
      setSummary(data.summary);
    } catch (e) { console.error('Errore caricamento registry:', e); }
    finally { setLoading(false); }
  }, []);

  const fetchCustomTasks = useCallback(async () => {
    try {
      const { data } = await api.get('/process/tasks/custom');
      setCustomTasks(data);
    } catch (e) { console.error('Errore caricamento task custom:', e); }
  }, []);

  useEffect(() => { fetchRegistry(); fetchCustomTasks(); }, [fetchRegistry, fetchCustomTasks]);

  const archiveCustomTask = async (taskId) => {
    try {
      await api.delete(`/process/tasks/custom/${taskId}`);
      setCustomTasks(prev => prev.filter(t => t.id !== taskId));
      toast.success('Task archiviata');
    } catch { toast.error('Errore archiviazione'); }
  };

  const runHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.post('/system/task-registry/health-check');
      setTasks(data.tasks);
      setSummary(data.summary);
      setChannels(data.channels || []);
      setIntegrations(data.integrations || {});
      setHealthMode(true);
    } catch (e) { console.error('Errore health check:', e); }
    finally { setHealthLoading(false); }
  };

  const toggleArea = (area) => setExpandedAreas(prev => ({ ...prev, [area]: !prev[area] }));

  const filtered = tasks.filter(t => {
    if (filterArea !== 'all' && t.area !== filterArea) return false;
    if (filterStatus !== 'all' && t.status !== filterStatus) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return t.task.toLowerCase().includes(q) || t.primary.toLowerCase().includes(q) || t.id.toLowerCase().includes(q);
    }
    return true;
  });

  const groupedByArea = filtered.reduce((acc, t) => {
    if (!acc[t.area]) acc[t.area] = [];
    acc[t.area].push(t);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="task-registry-loading">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="task-registry-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100 tracking-tight">Task Registry</h1>
          <p className="text-sm text-zinc-500 mt-1">Registro operativo completo &mdash; {tasks.length} procedure di sistema + {customTasks.length} custom</p>
        </div>
        <Button
          onClick={runHealthCheck}
          disabled={healthLoading}
          variant="outline"
          className="border-zinc-700 hover:border-emerald-500/50 hover:bg-emerald-500/10"
          data-testid="health-check-btn"
        >
          {healthLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
          Verifica Istantanea
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="sistema" data-testid="tab-sistema"
            className="data-[state=active]:bg-zinc-700 data-[state=active]:text-zinc-100">
            Sistema ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="custom" data-testid="tab-custom"
            className="data-[state=active]:bg-zinc-700 data-[state=active]:text-zinc-100">
            Custom ({customTasks.length})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'custom' && (
        <div className="space-y-4" data-testid="custom-tasks-panel">
          {customTasks.length === 0 ? (
            <div className="text-center py-12 text-zinc-500" data-testid="no-custom-tasks">
              <p>Nessuna task custom. Le task vengono create automaticamente dalle Situazioni nella Board Operativa.</p>
            </div>
          ) : (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800/40 text-zinc-500 text-xs">
                    <th className="px-4 py-2 text-left w-24">ID</th>
                    <th className="px-4 py-2 text-left">Procedura</th>
                    <th className="px-4 py-2 text-left hidden md:table-cell">Area</th>
                    <th className="px-4 py-2 text-left hidden md:table-cell">Responsabile</th>
                    <th className="px-4 py-2 text-left hidden lg:table-cell">Scadenza</th>
                    <th className="px-4 py-2 text-left hidden lg:table-cell">Ricorrenza</th>
                    <th className="px-4 py-2 text-center w-28">Stato</th>
                    <th className="px-4 py-2 text-center w-20">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {customTasks.map(t => (
                    <tr key={t.id} className="border-b border-zinc-800/20 hover:bg-zinc-800/20 transition-colors"
                        data-testid={`custom-task-row-${t.id}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{t.id}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-zinc-200">{t.task}</span>
                        {t.output_expected && <p className="text-xs text-zinc-500 mt-0.5 hidden xl:block">{t.output_expected}</p>}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-400 hidden md:table-cell">{t.area}</td>
                      <td className="px-4 py-2.5 text-zinc-400 hidden md:table-cell">{t.assignee || t.primary}</td>
                      <td className="px-4 py-2.5 text-zinc-500 hidden lg:table-cell">
                        {t.deadline ? (
                          <span className="flex items-center gap-1 text-xs">
                            <CalendarDays className="w-3 h-3" />{t.deadline.slice(0, 10)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2.5 text-zinc-500 hidden lg:table-cell">
                        {t.recurrence ? (
                          <span className="flex items-center gap-1 text-xs">
                            <RotateCw className="w-3 h-3" />{t.recurrence}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs">
                          {t.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Button variant="ghost" size="sm" onClick={() => archiveCustomTask(t.id)}
                          className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                          data-testid={`archive-task-${t.id}`}>
                          <Archive className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sistema' && (<>
      {/* Contenuto originale sistema */}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="task-registry-summary">
          <SummaryCard label="Totali" value={summary.total} color="text-zinc-100" />
          <SummaryCard label="Attive" value={summary.active} color="text-emerald-400" />
          <SummaryCard label="Parziali" value={summary.partial} color="text-amber-400" />
          <SummaryCard label="Da implementare" value={summary.pending_implementation} color="text-zinc-500" />
          <SummaryCard label="Futuro" value={summary.future} color="text-purple-400" />
        </div>
      )}

      {healthMode && channels.length > 0 && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4" data-testid="channel-health-panel">
          <h3 className="text-sm font-medium text-zinc-300 mb-3">Stato Canali di Dispatch</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {channels.map(ch => (
              <div key={ch.channel} className="flex items-center gap-2 text-sm">
                <span className={`w-2 h-2 rounded-full ${ch.status === 'ok' ? 'bg-emerald-400' : ch.status === 'error' ? 'bg-red-400' : 'bg-amber-400'}`} />
                <span className="text-zinc-300 capitalize">{ch.channel}</span>
                <span className="text-zinc-500 text-xs truncate">{ch.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Cerca task, agente, ID..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-900/50 border-zinc-800 text-zinc-200"
            data-testid="task-registry-search"
          />
        </div>
        <Select value={filterArea} onValueChange={setFilterArea}>
          <SelectTrigger className="w-[160px] bg-zinc-900/50 border-zinc-800 text-zinc-300" data-testid="filter-area">
            <SelectValue placeholder="Area" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le aree</SelectItem>
            {AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800 text-zinc-300" data-testid="filter-status">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filterArea !== 'all' || filterStatus !== 'all' || searchQuery) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterArea('all'); setFilterStatus('all'); setSearchQuery(''); }}
            className="text-zinc-500 hover:text-zinc-300" data-testid="clear-filters-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
        )}
      </div>

      <div className="space-y-2" data-testid="task-registry-list">
        {Object.entries(groupedByArea).map(([area, areaTasks]) => {
          const isOpen = expandedAreas[area] !== false;
          const activeCount = areaTasks.filter(t => t.status === 'active').length;
          return (
            <div key={area} className="bg-zinc-900/40 border border-zinc-800/60 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleArea(area)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/30 transition-colors"
                data-testid={`area-toggle-${area.toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                  <span className="text-sm font-medium text-zinc-200">{area}</span>
                  <span className="text-xs text-zinc-500">{areaTasks.length} task</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-emerald-400">{activeCount} attive</span>
                  <span className="text-xs text-zinc-600">/</span>
                  <span className="text-xs text-zinc-500">{areaTasks.length}</span>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-zinc-800/40">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800/40 text-zinc-500 text-xs">
                        <th className="px-4 py-2 text-left w-20">ID</th>
                        <th className="px-4 py-2 text-left">Procedura</th>
                        <th className="px-4 py-2 text-left hidden md:table-cell">Responsabile</th>
                        <th className="px-4 py-2 text-left hidden lg:table-cell">Backup</th>
                        <th className="px-4 py-2 text-left hidden lg:table-cell">Strumento</th>
                        <th className="px-4 py-2 text-center w-32">Stato</th>
                        {healthMode && <th className="px-4 py-2 text-center w-24">Health</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {areaTasks.map(t => {
                        const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.future;
                        const StatusIcon = sc.icon;
                        const hc = HEALTH_CONFIG[t.health] || HEALTH_CONFIG.ok;
                        return (
                          <tr key={t.id} className="border-b border-zinc-800/20 hover:bg-zinc-800/20 transition-colors"
                              data-testid={`task-row-${t.id}`}>
                            <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{t.id}</td>
                            <td className="px-4 py-2.5">
                              <span className="text-zinc-200">{t.task}</span>
                              {t.notes && <p className="text-xs text-zinc-500 mt-0.5 hidden xl:block">{t.notes}</p>}
                            </td>
                            <td className="px-4 py-2.5 text-zinc-400 hidden md:table-cell">{t.primary}</td>
                            <td className="px-4 py-2.5 text-zinc-500 hidden lg:table-cell">{t.backup}</td>
                            <td className="px-4 py-2.5 text-zinc-500 font-mono text-xs hidden lg:table-cell truncate max-w-[180px]">{t.tool}</td>
                            <td className="px-4 py-2.5 text-center">
                              <Badge variant="outline" className={`${sc.color} text-xs gap-1`}>
                                <StatusIcon className="w-3 h-3" /> {sc.label}
                              </Badge>
                            </td>
                            {healthMode && (
                              <td className="px-4 py-2.5 text-center">
                                <span className={`text-xs font-medium ${hc.color}`}>{hc.label}</span>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12 text-zinc-500" data-testid="no-tasks-found">
          <p>Nessuna procedura trovata con i filtri correnti.</p>
        </div>
      )}
      </>)}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-3">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className={`text-xl font-semibold ${color}`}>{value}</p>
    </div>
  );
}
